import React, { useState, useEffect, useMemo } from 'react';
import { DillButton, DillModal } from '../../../general/Dill';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import {
  blockedTradeReferenceEmailMessage,
  CreditApplicationFieldsValues,
  findBlockedTradeReferenceEmailDomain,
  getBlockedTradeReferenceEmailDomains,
} from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { openDillToast } from '../../../../utils/helpers';
import {
  updateApplicationUpdateHistory,
  updateSupplierCreditApplication,
} from '../../../../redux/services/creditApplicationsService';
import StateDropDown from './StateDropDown';
import moment from 'moment';

const UpdateTradeReferenceDetailsModal = ({
  open,
  handleClose,
  tradeReferenceDetails,
  creditApplicationId,
  applicationHistoryId,
}: {
  open: boolean;
  handleClose: () => void;
  tradeReferenceDetails: { type: 'email' | 'company'; field: CreditApplicationFieldsValues };
  creditApplicationId: string;
  applicationHistoryId?: string | null;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { creditApplication } = useAppSelector((state) => state.creditApplications);
  const [inputField, setInputField] = useState<string>('');
  const [errorFields, setErrorFields] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setInputField(
        typeof tradeReferenceDetails.field.value === 'string'
          ? tradeReferenceDetails.field.value
          : ''
      );
      setErrorFields({});
    }
  }, [open]);

  const refCompany = useMemo(() => {
    if (open) {
      const refrenceCompanies = creditApplication?.fieldValues.find(
        (field) =>
          field.id.includes('trade-reference-company-name') &&
          field.groupedPosition === tradeReferenceDetails.field.groupedPosition
      );
      return refrenceCompanies?.value ?? '';
    }
    return null;
  }, [open, creditApplication, tradeReferenceDetails]);

  const refTradeEmail = useMemo(() => {
    if (open && tradeReferenceDetails.type !== 'email') {
      const refrenceCompanyEmail = creditApplication?.fieldValues.find(
        (field) =>
          field.id.includes('trade-reference-company-email') &&
          field.groupedPosition === tradeReferenceDetails.field.groupedPosition
      );
      if (refrenceCompanyEmail) {
        return {
          ...refrenceCompanyEmail,
          referenceHistoryDate: [
            ...(refrenceCompanyEmail.referenceHistoryDate ?? []),
            {
              updated: moment().format('MM-DD-YYYY'),
              actionBy: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
              action: 'updated',
              note: `${tradeReferenceDetails.field.name} updated ${
                tradeReferenceDetails.field.value ? `from ${tradeReferenceDetails.field.value}` : ''
              } to ${inputField}`,
              date: moment().format('MM-DD-YYYY LT'),
            },
          ],
        };
      }
    }
    return null;
  }, [open, creditApplication, tradeReferenceDetails, inputField]);

  const validateInputs = () => {
    const errors: { [key: string]: string } = {};

    if (!inputField || inputField.trim() === '') {
      errors.inputField = `${tradeReferenceDetails.field.name} is required`;
    } else if (
      tradeReferenceDetails.type === 'email' &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputField)
    ) {
      errors.inputField = 'Invalid email format';
    } else if (
      tradeReferenceDetails.type === 'email' &&
      findBlockedTradeReferenceEmailDomain(
        inputField,
        getBlockedTradeReferenceEmailDomains(currentSupplier, [
          creditApplication?.senderEmail,
          creditApplication?.fieldValues.find((f) => f.id === 'email-personal')?.value?.toString(),
        ])
      )
    ) {
      errors.inputField = blockedTradeReferenceEmailMessage(currentSupplier?.name);
    }

    setErrorFields(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCloseModal = () => {
    setInputField('');
    setErrorFields({});
    handleClose();
  };

  const handleUpdateTradeReference = async () => {
    if (!validateInputs()) return;
    setIsLoading(true);
    const fields = [
      {
        ...tradeReferenceDetails.field,
        value: inputField,
        ...(tradeReferenceDetails.type === 'email' ? { status: 'Requested' as const } : {}),
        referenceHistoryDate: [
          ...(tradeReferenceDetails?.field?.referenceHistoryDate ?? []),

          {
            updated: moment().format('MM-DD-YYYY'),
            actionBy: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
            action: 'updated',
            note: `${tradeReferenceDetails.field.name} updated ${
              tradeReferenceDetails.field.value ? `from ${tradeReferenceDetails.field.value}` : ''
            } to ${inputField}`,
            date: moment().format('MM-DD-YYYY LT'),
          },

          ...(tradeReferenceDetails.type === 'email'
            ? [
                {
                  requested: moment().format('MM-DD-YYYY'),
                  actionBy: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
                  action: 'requested',
                  note: `Requested trade reference from ${inputField}`,
                  date: moment().format('MM-DD-YYYY LT'),
                },
              ]
            : []),
        ],
      },
      ...(refTradeEmail ? [refTradeEmail] : []),
    ];

    let results = null;
    if (!applicationHistoryId) {
      results = await dispatch(
        updateSupplierCreditApplication({
          creditApplicationId: creditApplicationId,
          supplierId: currentSupplier?.id || '',
          fieldValues: fields,
          savingType: `Trade reference updated - ${
            tradeReferenceDetails.type === 'email'
              ? `__refCompany__${refCompany}__refCompany__${inputField}`
              : ''
          }`,
        })
      );
    } else {
      results = await dispatch(
        updateApplicationUpdateHistory({
          creditApplicationId: creditApplicationId,
          applicationHistoryId: applicationHistoryId,
          supplierId: currentSupplier?.id || '',
          fieldValues: fields,
        })
      );
    }
    if (results && results.type.includes('fulfilled')) {
      openDillToast({
        type: 'SUCCESS',
        message: `${tradeReferenceDetails.field.name} successfully updated ${
          tradeReferenceDetails.type === 'email' ? 'and resent email' : ''
        }.`,
      });

      handleCloseModal();
    } else {
      openDillToast({
        type: 'ERROR',
        message: `Failed to update ${tradeReferenceDetails.field.name}`,
      });
    }
    setIsLoading(false);
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title={`Update ${
        tradeReferenceDetails.type === 'email' ? '& Send Email' : tradeReferenceDetails.field.name
      }`}
      maxWidth="540px"
      footer={
        <div className="flex justify-end w-full gap-3">
          <DillButton
            label="Cancel"
            variant="ghost"
            onClick={handleCloseModal}
            isDisabled={isLoading}
          />
          <DillButton
            label={tradeReferenceDetails.type === 'email' ? 'Update & Send' : 'Update'}
            onClick={handleUpdateTradeReference}
            isDisabled={isLoading}
            isLoading={isLoading}
          />
        </div>
      }>
      <div className="w-full">
        {tradeReferenceDetails.field.fromField === 'STATE_DROPDOWN' ? (
          <StateDropDown
            isRequired
            label={tradeReferenceDetails.field.name}
            value={inputField}
            errorText={errorFields.inputField || ''}
            onTextChange={(text) => {
              setInputField(text);
              setErrorFields((prev) => {
                const newErrors = { ...prev };
                delete newErrors.inputField;
                return newErrors;
              });
            }}
          />
        ) : (
          <AppInputField
            id="inputField"
            label={tradeReferenceDetails.field.name}
            isRequired
            placeholder={`Enter ${tradeReferenceDetails.field.name}`}
            errorText={errorFields.inputField || ''}
            value={inputField}
            onTextChange={(text) => {
              setInputField(text);
              setErrorFields((prev) => {
                const newErrors = { ...prev };
                delete newErrors.inputField;
                return newErrors;
              });
            }}
          />
        )}
      </div>
    </DillModal>
  );
};

export default UpdateTradeReferenceDetailsModal;
